import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';

export const AboutPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.about.title')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('pages.about.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('pages.about.description')}
          </p>
        </div>
      </section>

      {/* Content Sections */}
      <section className="section relative">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {(Array.isArray(t('pages.about.sections', { returnObjects: true })) ? t('pages.about.sections', { returnObjects: true }) as any[] : []).map((section: any, index: number) => (
              <div key={index} className="modern-card group relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`fas fa-${index === 0 ? 'bullseye' : index === 1 ? 'cogs' : 'eye'} text-3xl text-gold`}></i>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-6 text-center group-hover:text-gold transition-colors duration-300">
                  {section.title}
                </h3>

                <p className="text-gray-400 leading-relaxed text-center">
                  {section.body}
                </p>

                <div className="absolute bottom-4 left-4 right-4">
                  <div className="h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.about.whyChoose.subtitle')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.about.whyChoose.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('pages.about.whyChoose.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {(Array.isArray(t('pages.about.whyChoose.features', { returnObjects: true })) ? t('pages.about.whyChoose.features', { returnObjects: true }) as any[] : []).map((feature: any, index: number) => (
              <div key={index} className="glass-card text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <i className={`fas fa-${index === 0 ? 'shield-alt' : index === 1 ? 'chart-line' : index === 2 ? 'coins' : 'globe'} text-4xl text-gold`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.about.tech.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.about.tech.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('pages.about.tech.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {(Array.isArray(t('pages.about.tech.items', { returnObjects: true })) ? t('pages.about.tech.items', { returnObjects: true }) as any[] : []).map((tech: any, index: number) => (
              <div key={index} className="modern-card group">
                <div className="feature-icon">
                  <i className={`fas fa-${index === 0 ? 'network-wired' : index === 1 ? 'lock' : 'cube'} text-3xl text-gold group-hover:scale-110 transition-transform`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{tech.title}</h3>
                <p className="text-gray-400 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section pb-20">
        <div className="container">
          <div className="cta-section relative">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 relative z-10">{t('pages.about.cta.title')}</h2>
            <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10">
              {t('pages.about.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center relative z-10">
              <Link to="/buy">
                <Button variant="primary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">{t('pages.about.cta.primaryBtn')}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
              <Link to="/resources">
                <Button variant="secondary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">{t('pages.about.cta.secondaryBtn')}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};